import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Eye, Tag, User } from 'lucide-react';

const BlogPreview = () => {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load preview data from sessionStorage
    const previewData = sessionStorage.getItem('blogPreview');
    if (previewData) {
      setBlog(JSON.parse(previewData));
    }
    setLoading(false);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const categoryLabels = {
      GENERAL: 'General',
      COFFEE_FACTS: 'Coffee Facts',
      RECIPES: 'Recipes',
      TIPS: 'Tips',
      REVIEWS: 'Reviews',
      NEWS: 'News',
      LIFESTYLE: 'Lifestyle'
    };
    return categoryLabels[category] || category;
  };

  const closePreview = () => {
    window.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">No Preview Available</h1>
          <p className="text-gray-300 mb-6">No blog preview data found.</p>
          <button
            onClick={closePreview}
            className="px-4 py-2 bg-[#a15507] text-white rounded-lg hover:bg-[#924e06]"
          >
            Close Preview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]">
      {/* Preview Header */}
      <div className="sticky top-0 z-50 bg-[#a15507]/90 backdrop-blur-sm border-b border-[#924e06]">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <span className="text-white font-medium">Preview Mode</span>
            </div>
            <button
              onClick={closePreview}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Close Preview
            </button>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Featured Image */}
        {blog.featuredImageUrl && (
          <div className="mb-8">
            <img
              src={blog.featuredImageUrl}
              alt={blog.title}
              className="w-full h-64 md:h-80 object-cover rounded-xl"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Category Badge */}
        <div className="mb-6">
          <span className="inline-flex items-center px-3 py-1 bg-[#a15507] text-white text-sm font-medium rounded-full">
            {getCategoryLabel(blog.category)}
          </span>
          {blog.featured && (
            <span className="inline-flex items-center px-3 py-1 bg-yellow-600 text-white text-sm font-medium rounded-full ml-2">
              Featured
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
          {blog.title}
        </h1>

        {/* Excerpt */}
        {blog.excerpt && (
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            {blog.excerpt}
          </p>
        )}

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-gray-400 text-sm mb-12 pb-8 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>Admin User</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(blog.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>{blog.viewCount || 0} views</span>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          <div
            className="text-gray-200 leading-relaxed"
            dangerouslySetInnerHTML={require('../utils/security').sanitizedHtml(blog.content)}
          />
        </div>

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex items-center gap-3 flex-wrap">
              <Tag className="w-4 h-4 text-gray-400" />
              {blog.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full hover:bg-gray-600 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preview Footer */}
        <div className="mt-16 pt-8 border-t border-gray-700">
          <div className="bg-gray-800/50 rounded-lg p-6">
            <h3 className="text-white font-medium mb-2">Preview Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-400">
              <div>
                <span className="font-medium">Status:</span> {blog.status}
              </div>
              <div>
                <span className="font-medium">Slug:</span> /{blog.slug}
              </div>
              {blog.metaTitle && (
                <div className="md:col-span-2">
                  <span className="font-medium">SEO Title:</span> {blog.metaTitle}
                </div>
              )}
              {blog.metaDescription && (
                <div className="md:col-span-2">
                  <span className="font-medium">SEO Description:</span> {blog.metaDescription}
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      {/* Custom styles for content */}
      <style jsx global>{`
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          color: #ffffff;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        
        .prose h1 {
          font-size: 2.25rem;
          line-height: 1.2;
        }
        
        .prose h2 {
          font-size: 1.875rem;
          line-height: 1.3;
        }
        
        .prose h3 {
          font-size: 1.5rem;
          line-height: 1.4;
        }
        
        .prose p {
          margin-bottom: 1.5rem;
          line-height: 1.75;
        }
        
        .prose ul, .prose ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        
        .prose li {
          margin-bottom: 0.5rem;
        }
        
        .prose blockquote {
          border-left: 4px solid #a15507;
          background-color: rgba(161, 85, 7, 0.1);
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        
        .prose code {
          background-color: #374151;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
        }
        
        .prose pre {
          background-color: #1f2937;
          padding: 1.5rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        
        .prose img {
          border-radius: 0.5rem;
          margin: 1.5rem 0;
        }
        
        .prose a {
          color: #a15507;
          text-decoration: none;
        }
        
        .prose a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default BlogPreview;
